import { useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useForm } from 'react-hook-form'

import { ApolloCache } from '@apollo/client'
import { F0Dialog } from '@factorialco/f0-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { subDays } from 'date-fns'
import {
  ShiftManagementBulkCopyUnassignedShiftsDocument,
  ShiftManagementBulkGenerateCoverageDocument,
  ShiftManagementCoverageSectionDocument,
  ShiftManagementDeleteCoverageDocument,
  ShiftManagementUnassignedShiftFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { SuccessDialog } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/Body/GenerateCoverage/Content/SuccessDialog'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { DAY_IN_MS } from 'modules/shift_management/lib/consts/ShiftManagementFormats'
import { buildDateRange } from 'modules/shift_management/lib/dateFormatter'
import {
  GENERATION_METHODS,
  GenerateCoverageForm,
  SchemaType,
} from 'modules/shift_management/lib/forms/GenerateCoverageForm'
import type { View } from 'modules/shift_management/lib/types'
import { removeUnassignedShiftsCoverageCache } from 'modules/shift_management/utils/apolloCache/unassignedShiftsCoverage'

import { Content } from './Content'

interface Props {
  onClose: () => void
  view: View
  setShowAutoScheduleModal: (show: boolean) => void
}

const GenerateCoverageModal = observer(({ view, onClose, setShowAutoScheduleModal }: Props) => {
  const [submitDisabled, setSubmitDisabled] = useState(false)
  const [showGenerateCoverageSuccessModal, setShowGenerateCoverageSuccessModal] =
    useState<boolean>(false)
  const [formVisible, setFormVisible] = useState<boolean>(true)
  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()
  const [{ locationIds }] = useFilters()
  const currentLocation = locationIds.at(0)

  const { defaultValues, schema } = GenerateCoverageForm()
  const form = useForm<SchemaType>({
    defaultValues,
    resolver: zodResolver(schema),
  })

  const [deleteCoverage] = useMutation(ShiftManagementDeleteCoverageDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementUnassignedShift,
    update: (cache, { data }) => {
      const deletedShifts =
        data?.shiftManagementMutations.bulkDeleteShiftmanagementUnassignedShift.unassignedShifts ||
        []

      removeUnassignedShiftsCoverageCache({
        cache,
        unassignedShiftIds: deletedShifts.map((shift) => shift.id),
      })
    },
  })

  const update = (
    cache: ApolloCache<unknown>,
    unassignedShifts?: ShiftManagementUnassignedShiftFragment[]
  ) => {
    if (!unassignedShifts || unassignedShifts.length === 0) return

    const endAt = buildDateRange(
      form.getValues('targetStartAt'),
      form.getValues('daysToGenerate'),
      DAY_IN_MS
    )

    const locationFormValue = form.getValues('locationIds')?.at(0)

    const queryOptions = {
      query: ShiftManagementCoverageSectionDocument,
      variables: {
        locationId: locationFormValue ? locationFormValue : currentLocation,
        startAt: i18nDateToString(form.getValues('targetStartAt'), 'backend'),
        endAt: i18nDateToString(endAt, 'backend'),
      },
    }

    cache.writeQuery({
      ...queryOptions,
      data: {
        shiftManagement: { unassignedShifts },
      },
    })
  }

  const [generateFromShifts, { loading }] = useMutation(
    ShiftManagementBulkGenerateCoverageDocument,
    {
      extractorFn: (data) =>
        data.shiftManagementMutations.bulkGenerateShiftmanagementUnassignedShift,
      update: (cache, { data }) => {
        const newUnassignedShifts =
          data?.shiftManagementMutations?.bulkGenerateShiftmanagementUnassignedShift
            .unassignedShifts

        update(cache, newUnassignedShifts || [])
      },
    }
  )
  const [generateFromPreviousCoverage, { loading: copyUnassignedShiftsLoading }] = useMutation(
    ShiftManagementBulkCopyUnassignedShiftsDocument,
    {
      extractorFn: (data) => data.shiftManagementMutations.bulkCopyShiftmanagementUnassignedShift,
      update: (cache, { data }) => {
        const newUnassignedShifts =
          data?.shiftManagementMutations.bulkCopyShiftmanagementUnassignedShift.unassignedShifts

        update(cache, newUnassignedShifts || [])
      },
    }
  )

  useUnsafeEffect(() => {
    const currentDate = new Date(view.startAt)
    const oneWeekAgo = subDays(currentDate, 7)

    form.setValue('originalStartAt', oneWeekAgo)
    form.setValue('targetStartAt', currentDate)
  }, [view.startAt])

  const submitFromShifts = async (data: SchemaType) => {
    const locationFormValue = form.getValues('locationIds')
    const variables = {
      daysToGenerate: data.daysToGenerate + 1 || 0,
      includeDraftShifts: true,
      locationIds: locationFormValue ? locationFormValue : [currentLocation ?? ''],
      originalStartAt: i18nDateToString(data.originalStartAt, 'backend'),
      targetStartAt: i18nDateToString(data.targetStartAt, 'backend'),
    }
    const res = await generateFromShifts({ variables })
    const handled = handleResult(res, () => {})
    return handled
  }

  const submitFromPreviousCoverage = async (data: SchemaType) => {
    const locationFormValue = form.getValues('locationIds')
    const variables = {
      companyId: currentCompanyId,
      daysToCopy: data.daysToGenerate + 1,
      locationIds: locationFormValue ? locationFormValue : [currentLocation ?? ''],
      originalStartAt: i18nDateToString(data.originalStartAt, 'backend'),
      targetStartAt: i18nDateToString(data.targetStartAt, 'backend'),
    }
    const res = await generateFromPreviousCoverage({ variables })
    const handled = handleResult(res, () => {})
    return handled
  }

  const onSubmit = async (data: SchemaType) => {
    let res
    const endAt = buildDateRange(data.targetStartAt, data.daysToGenerate, DAY_IN_MS)
    await deleteCoverage({
      variables: {
        endAt: i18nDateToString(endAt, 'backend'),
        locationIds: currentLocation ? [currentLocation] : [],
        startAt: i18nDateToString(data.targetStartAt, 'backend'),
      },
    })
    if (data.generationMethod === GENERATION_METHODS.from_shifts) {
      res = await submitFromShifts(data)
    } else {
      res = await submitFromPreviousCoverage(data)
    }

    if (!res?.success) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('shift_management.coverage_section.generate_coverage_modal.error'),
      })
      return
    }
    track(trackingEvents.shiftManagement.generateCoverageSubmit, {
      generation_method: data.generationMethod,
      reference_period: String(data.originalStartAt),
      date_date: String(data.targetStartAt),
      location_id: data.locationIds?.at(0),
    })
    setShowGenerateCoverageSuccessModal(true)
    setFormVisible(false)
  }

  return (
    <>
      {showGenerateCoverageSuccessModal && (
        <SuccessDialog
          showGenerateCoverageSuccessModal={showGenerateCoverageSuccessModal}
          onClose={() => {
            setShowGenerateCoverageSuccessModal(false)
            onClose()
          }}
          form={form}
          setShowAutoScheduleModal={setShowAutoScheduleModal}
        />
      )}
      <F0Dialog
        isOpen={formVisible}
        onClose={() => {
          setFormVisible(false)
          onClose()
        }}
        position="center"
        title={i18n.t('shift_management.coverage_section.generate_coverage_modal.title')}
        primaryAction={{
          label: i18n.t('shift_management.coverage_section.generate_coverage_modal.submit'),
          onClick: form.handleSubmit(onSubmit),
          disabled: submitDisabled,
          loading: loading || copyUnassignedShiftsLoading,
        }}
      >
        <FormProvider {...form}>
          <Content
            currentLocation={currentLocation?.toString() || ''}
            setSubmitDisabled={setSubmitDisabled}
          />
        </FormProvider>
      </F0Dialog>
    </>
  )
})

export { GenerateCoverageModal }
